"use client";
import { getVacationDaysRemaining, teamIsOnVacation } from "@/lib/utils";
import { Match, Team, TeamStatus } from "@/types";
import { format, formatDistance, formatRelative } from "date-fns";
import { enUS, fi } from "date-fns/locale";
import { useLocale, useTranslations } from "next-intl";
import { memo, ReactNode, useEffect, useState } from "react";

interface TeamStatusCardProps {
  team: Team;
  activeMatch?: Match | null;
  opposingTeamName?: string | null;
  /** Optional action content (buttons) to render inside the card */
  children?: ReactNode;
  /** Rendering style — "card" (default) or "inline" single-line */
  variant?: "card" | "inline";
}

const statusConfig: Record<
  TeamStatus,
  {
    icon: string;
    bgColor: string;
    textColor: string;
    borderColor: string;
  }
> = {
  [TeamStatus.INJURED]: {
    icon: "🩹",
    bgColor: "bg-red-500/5",
    textColor: "text-red-400",
    borderColor: "border-red-500/20",
  },
  [TeamStatus.ON_VACATION]: {
    icon: "🏖️",
    bgColor: "bg-amber-500/5",
    textColor: "text-amber-400",
    borderColor: "border-amber-500/20",
  },
  [TeamStatus.CHALLENGE]: {
    icon: "⚔️",
    bgColor: "bg-purple-500/5",
    textColor: "text-purple-400",
    borderColor: "border-purple-500/20",
  },
  [TeamStatus.MATCH_SCHEDULED]: {
    icon: "🗓️",
    bgColor: "bg-blue-500/5",
    textColor: "text-blue-400",
    borderColor: "border-blue-500/20",
  },
  [TeamStatus.AVAILABLE]: {
    icon: "✓",
    bgColor: "bg-emerald-500/5",
    textColor: "text-emerald-400",
    borderColor: "border-emerald-500/20",
  },
  [TeamStatus.JOIN_REQUEST]: {
    icon: "📝",
    bgColor: "bg-white/[0.02]",
    textColor: "text-white/60",
    borderColor: "border-white/10",
  },
  [TeamStatus.DISABLED]: {
    icon: "🚫",
    bgColor: "bg-white/[0.02]",
    textColor: "text-white/40",
    borderColor: "border-white/10",
  },
  [TeamStatus.FORFEIT]: {
    icon: "🏳️",
    bgColor: "bg-rose-500/5",
    textColor: "text-rose-400",
    borderColor: "border-rose-500/20",
  },
};

/**
 * TeamStatusCard - Displays team status banner (injured, vacation, challenged, scheduled)
 */
export const TeamStatusCard = memo(function TeamStatusCard({
  team,
  activeMatch,
  opposingTeamName,
  children,
  variant = "card",
}: TeamStatusCardProps) {
  const t = useTranslations();
  const locale = useLocale();
  const dateFnsLocale = locale === "fi" ? fi : enUS;
  // Prevent hydration mismatch by deferring time-based calculations
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const now = mounted ? new Date() : new Date(0); // Use epoch as stable initial value
  const isOnVacation = teamIsOnVacation(team);
  const vacationDaysRemaining = isOnVacation
    ? getVacationDaysRemaining(team)
    : 0;
  const config =
    statusConfig[isOnVacation ? TeamStatus.ON_VACATION : team.status];

  // Get all vacation periods if team is on vacation
  const vacationPeriods = isOnVacation && team.vacations ? team.vacations : [];

  // Get future scheduled vacations (when team is available and NOT on vacation)
  // Only calculate after mount to prevent hydration mismatch
  const futureVacations =
    mounted &&
    !isOnVacation &&
    team.status === TeamStatus.AVAILABLE &&
    team.vacations
      ? team.vacations.filter((vacation) => new Date(vacation.start) > now)
      : [];

  if (variant === "inline") {
    const hasFutureVacations =
      !isOnVacation &&
      team.status === TeamStatus.AVAILABLE &&
      futureVacations.length > 0;
    const inlineIcon = hasFutureVacations ? "📅️" : config.icon;
    const inlineColor = hasFutureVacations
      ? "text-amber-400"
      : config.textColor;

    let title: string | null = null;
    let detail: ReactNode = null;
    if (team.status === TeamStatus.INJURED) {
      title = t("teams.status.injured");
    } else if (team.status === TeamStatus.FORFEIT) {
      title = t("teams.status.forfeit");
      detail = t("teams.forfeit.cannotPlay");
    } else if (isOnVacation) {
      title = t("teams.status.on_vacation");
      detail = t("teams.vacationDaysRemaining", {
        days: vacationDaysRemaining,
      });
    } else if (hasFutureVacations) {
      const first = futureVacations[0];
      title = t("myTeam.vacation.scheduledVacations");
      detail = `${format(new Date(first.start), "dd.MM.yyyy HH:mm")} – ${format(
        new Date(first.end),
        "dd.MM.yyyy HH:mm",
      )}`;
    } else if (team.status === TeamStatus.CHALLENGE) {
      title = t("teams.status.challenge");
      if (opposingTeamName) {
        detail =
          activeMatch?.team1Id === team.id
            ? t("teams.challengingTeam", { teamName: opposingTeamName })
            : t("teams.challengedBy", { teamName: opposingTeamName });
      }
    } else if (team.status === TeamStatus.MATCH_SCHEDULED) {
      title = t("teams.status.match_scheduled");
      if (opposingTeamName) {
        detail = t("teams.scheduledMatchWith", { teamName: opposingTeamName });
      }
    }

    if (!title) return null;

    return (
      <div
        className={`flex items-center justify-center gap-1.5 text-xs ${inlineColor}`}
      >
        <span aria-hidden>{inlineIcon}</span>
        <span className="truncate">
          <span className="font-medium">{title}:</span>
          {detail && <span className="ml-1 opacity-80">{detail}</span>}
        </span>
      </div>
    );
  }

  // If team is available (not on vacation) but has future vacations, show a custom card
  if (
    !isOnVacation &&
    team.status === TeamStatus.AVAILABLE &&
    futureVacations.length > 0
  ) {
    return (
      <div className="rounded-xl bg-amber-500/5 border border-amber-500/20 p-2.5">
        <div className="flex items-start gap-2.5">
          <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-amber-500/10 flex items-center justify-center">
            <span className="text-xl">📅️</span>
          </div>
          <div className="flex-1 min-w-0">
            <span className="text-md font-medium text-amber-400">
              {t("myTeam.vacation.scheduledVacations")}
            </span>
            <div className="text-sm text-white/60 mt-0.5">
              {futureVacations.map((vacation) => (
                <div key={vacation.id}>
                  {format(new Date(vacation.start), "dd.MM.yyyy HH:mm")} -{" "}
                  {format(new Date(vacation.end), "dd.MM.yyyy HH:mm")}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`rounded-xl ${config.bgColor} ${config.borderColor} border p-2.5`}
    >
      <div className="flex items-start gap-2.5">
        <div
          className={`flex-shrink-0 w-12 h-12 rounded-lg ${config.bgColor} flex items-center justify-center`}
        >
          <span className="text-xl">{config.icon}</span>
        </div>
        <div className="flex-1 min-w-0">
          <span className={`text-sm font-medium ${config.textColor}`}>
            {team.status === TeamStatus.INJURED && t("teams.status.injured")}
            {team.status === TeamStatus.FORFEIT && t("teams.status.forfeit")}
            {isOnVacation && t("teams.status.on_vacation")}
            {team.status === TeamStatus.CHALLENGE &&
              opposingTeamName &&
              (activeMatch?.team1Id === team.id
                ? t("teams.challengingTeam", { teamName: opposingTeamName })
                : t("teams.challengedBy", { teamName: opposingTeamName }))}
            {team.status === TeamStatus.CHALLENGE &&
              !opposingTeamName &&
              t("teams.status.challenge")}
            {team.status === TeamStatus.MATCH_SCHEDULED &&
              opposingTeamName &&
              t("teams.scheduledMatchWith", { teamName: opposingTeamName })}
            {team.status === TeamStatus.MATCH_SCHEDULED &&
              !opposingTeamName &&
              t("teams.status.match_scheduled")}
          </span>
          <div className="text-xs text-white/60 mt-0.5">
            {team.status === TeamStatus.FORFEIT && (
              <span className="text-rose-400/80">
                {t("teams.forfeit.cannotPlay")}
              </span>
            )}
            {team.status === TeamStatus.INJURED && team.injuredFrom && (
              <>
                {formatRelative(team.injuredFrom, now, {
                  locale: dateFnsLocale,
                })}
                <span className="text-white/50 ml-1.5">
                  (
                  {formatDistance(team.injuredFrom, new Date(), {
                    addSuffix: true,
                    locale: dateFnsLocale,
                  })}
                  )
                </span>
              </>
            )}
            {isOnVacation && vacationPeriods.length > 0 && (
              <div className="flex flex-col">
                <div className="text-amber-400/80 font-medium">
                  {t("teams.vacationDaysRemaining", {
                    days: vacationDaysRemaining,
                  })}
                </div>
                {vacationPeriods.map((vacation, index) => (
                  <div key={vacation.id}>
                    {format(new Date(vacation.start), "dd.MM.yyyy HH:mm")} -{" "}
                    {format(new Date(vacation.end), "dd.MM.yyyy HH:mm")}
                    {index < vacationPeriods.length - 1 && ", "}
                  </div>
                ))}
              </div>
            )}
            {team.status === TeamStatus.CHALLENGE &&
              activeMatch &&
              t("teams.challengeExpires", {
                date: formatRelative(activeMatch.challengeExpiresAt, now, {
                  locale: dateFnsLocale,
                }),
              })}
            {team.status === TeamStatus.MATCH_SCHEDULED &&
              activeMatch?.matchDate &&
              t("teams.scheduledFor", {
                date: formatRelative(activeMatch.matchDate, now, {
                  locale: dateFnsLocale,
                }),
              })}
          </div>
        </div>
      </div>
      {/* Action buttons (schedule match / submit results) */}
      {children && <div className="mt-3">{children}</div>}
    </div>
  );
});
